/**
 * Settings → API connections (operator portal rework PR 9, migration 0213). TENANT_ADMIN only
 * (VIEW_OPERATOR_API_CREDENTIALS on the API).
 *
 * A READ-ONLY list of the operator API keys that read this hauler's data. Since 0213 the hauler
 * no longer issues, rotates or revokes these keys: Paelode and the operator's admin do, and the
 * hauler's POST routes answer 403 OPERATOR_API_KEYS_MANAGED_BY_PAELODE. Corbin's decision: no
 * buttons that change a key here at all, so the only control is the "Show revoked" filter.
 *
 * Times render in the user's profile time zone (DEC-004). No ids are shown: the operator
 * company name and the key's label identify it.
 *
 * API: GET /v1/operator-api-credentials?includeRevoked=true|false
 */

import * as React from 'react';
import { useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type { OperatorApiCredentialView } from '@lodex-pro/shared/validation/operator-api-credentials';
import { formatTimestamp } from '../../lib/timestamp';
import { useUserTimezone } from '../../lib/use-user-timezone';
import { cardStyle, helpText } from '../load-emails/styles';

export const API_CONNECTIONS_KEY = (includeRevoked: boolean): readonly unknown[] => [
  'operator-api-credentials',
  { includeRevoked },
];

/** The sentence above the list (design §5: the hauler is told who manages the keys). */
export const API_CONNECTIONS_MANAGED_TEXT =
  "API keys that read your company's data are managed by Paelode and the operator. Contact Paelode to change one.";

/** Who issued a key, in words. HAULER = a key the hauler itself issued before 0213. */
export function issuedByLabel(actor: OperatorApiCredentialView['issuedByActor']): string {
  switch (actor) {
    case 'OPERATOR':
      return 'Operator';
    case 'PLATFORM':
      return 'Paelode';
    case 'HAULER':
      return 'Your company';
  }
}

/** Active, Revoked, Replaced (expires <time> during the overlap) or Expired, from the row. */
export function connectionStatus(
  c: Pick<OperatorApiCredentialView, 'status' | 'expiresAt' | 'rotatedToId'>,
  timeZone: string,
  now: Date = new Date(),
): string {
  if (c.status === 'REVOKED') return 'Revoked';
  const expired = c.expiresAt !== null && new Date(c.expiresAt).getTime() <= now.getTime();
  if (c.rotatedToId !== null) {
    if (c.expiresAt !== null && !expired) {
      return `Replaced, expires ${formatTimestamp(c.expiresAt, timeZone)}`;
    }
    return 'Replaced';
  }
  if (expired) return 'Expired';
  return 'Active';
}

const TH: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: '14px',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
};
const TD: React.CSSProperties = { padding: '10px 12px', fontSize: '15px' };

export function ApiConnectionsSection(): React.JSX.Element {
  const [showRevoked, setShowRevoked] = React.useState(false);
  const timeZone = useUserTimezone();
  const { data, isLoading, isError } = useApiQuery<{
    credentials: readonly OperatorApiCredentialView[];
  }>(useApiOpts(), `/v1/operator-api-credentials?includeRevoked=${String(showRevoked)}`, {
    queryKey: API_CONNECTIONS_KEY(showRevoked),
    staleTime: 30_000,
  });
  const credentials = data?.credentials;

  return (
    <section aria-labelledby="api-connections-heading" style={cardStyle}>
      <h2 id="api-connections-heading" style={{ fontSize: '18px', margin: '0 0 4px' }}>
        Operator API keys
      </h2>
      <p style={helpText} data-testid="api-connections-managed">
        {API_CONNECTIONS_MANAGED_TEXT}
      </p>
      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: '16px',
          minHeight: '44px',
          fontSize: '16px',
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={showRevoked}
          onChange={(e): void => setShowRevoked(e.target.checked)}
          style={{ width: '20px', height: '20px' }}
        />
        Show revoked
      </label>

      {isLoading && <p style={helpText}>Loading…</p>}
      {isError && (
        <p role="alert" style={{ ...helpText, color: 'var(--color-danger)' }}>
          Could not load API connections.
        </p>
      )}
      {credentials !== undefined && credentials.length === 0 && (
        <p style={helpText} data-testid="api-connections-empty">
          {showRevoked
            ? 'No operator has had an API key for your company.'
            : 'No operator has an API key for your company.'}
        </p>
      )}
      {credentials !== undefined && credentials.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: '12px' }}>
          <table aria-label="API connections" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
                <th style={TH}>Operator</th>
                <th style={TH}>Label</th>
                <th style={TH}>Status</th>
                <th style={TH}>Last used</th>
                <th style={TH}>Issued by</th>
              </tr>
            </thead>
            <tbody>
              {credentials.map((c) => (
                <tr key={c.id} style={{ borderTop: '1px solid var(--color-border-primary)' }}>
                  <td style={TD}>{c.operatorTenantName}</td>
                  <td style={TD}>{c.label}</td>
                  <td style={TD}>{connectionStatus(c, timeZone)}</td>
                  <td style={{ ...TD, color: 'var(--color-text-secondary)' }}>
                    {c.lastUsedAt === null ? 'Never' : formatTimestamp(c.lastUsedAt, timeZone)}
                  </td>
                  <td style={TD}>{issuedByLabel(c.issuedByActor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
